'use client'
import Asset from '~/cms/components/Asset'
import {DatoThemeSelector} from '~/cms/components/DatoThemeSelector'
import {DatoThemeSwitcher} from '~/cms/components/DatoThemeSwitcher'
import {SingleLineStructuredText} from '~/cms/components/SingleLineStructuredText'
import {HgHero, type HgHeroBaseProps} from '~/design-system/hg/modules'
import {safeUnreachableCase} from '~/utils/unreachableCase'
import {type HeroSectionProps} from './_types'
import {
  getCTATypeProps,
  getLogo,
  getLogoFromLogoAsset,
  structuredTextCharacterCount,
  getCinematicHeroProps,
} from './_utils'
import DisclaimerStructuredText from '~/cms/pages/CMSPageLayout/_components/Disclaimers/DisclaimerStructuredText'
import {AdditionalContent} from './_components/AdditionalContent'
import type {HgIconType} from '~/design-system/hg/components'

export default function HeroSection({data, subsequentSection}: HeroSectionProps) {
  const {variant, disclaimer, title, subhead, theme, alignment, breadcrumbs} = data

  const defaultProps: HgHeroBaseProps = {
    ...getCTATypeProps(data),
    headline: <SingleLineStructuredText data={title} />,
    subhead: <SingleLineStructuredText data={subhead} />,
    disclaimer: disclaimer && <DisclaimerStructuredText data={disclaimer.text} />,
    alignment: alignment as HgHeroBaseProps['alignment'],
    breadcrumbs: breadcrumbs?.map(({link, text}) => ({
      link,
      label: <SingleLineStructuredText data={text} />,
    })),
  }

  if (!variant) {
    return (
      <DatoThemeSwitcher theme={theme}>
        <section data-section="hero">
          <HgHero
            {...defaultProps}
            type="headingOnly"
            subsequentSection={subsequentSection}
          />
        </section>
      </DatoThemeSwitcher>
    )
  }

  switch (variant.__typename) {
    case 'CinematicHeroComponentRecord':
    case 'CinematicStackedComponentRecord':
    case 'CinematicOverlayComponentRecord': {
      const cinematicProps = getCinematicHeroProps(variant)
      if (!cinematicProps) return null

      return (
        <DatoThemeSwitcher theme={theme} applyGlobally={false}>
          <div>
            <DatoThemeSelector theme={theme}>
              <section data-section="hero">
                <HgHero type="cinematic" {...cinematicProps} {...defaultProps} />
              </section>
            </DatoThemeSelector>
          </div>
        </DatoThemeSwitcher>
      )
    }
    case 'SplitImageHeroComponentRecord':
      return (
        <DatoThemeSwitcher theme={theme}>
          <section data-section="hero">
            <HgHero
              {...defaultProps}
              type="splitImage"
              size={
                !subhead || structuredTextCharacterCount(subhead.text) < 200
                  ? 'short'
                  : 'long'
              }
              logoAsset={getLogoFromLogoAsset(variant.logoAsset ?? undefined)}
              assetOneOneRatio={
                variant.oneOneAsset && (
                  <Asset data={variant.oneOneAsset} aspectRatio="1:1" />
                )
              }
              assetThreeTwoRatio={
                variant.threeTwoAsset && (
                  <Asset data={variant.threeTwoAsset} aspectRatio="3:2" />
                )
              }
              assetFourFiveRatio={null}
              heroBullets={variant.heroBullets?.heroBulletItems.map(bullet => ({
                id: bullet.id,
                iconType: bullet.icon.iconType as HgIconType,
                text: <SingleLineStructuredText data={bullet.body} />,
              }))}
              additionalContent={
                <AdditionalContent content={variant.additionalContent} />
              }
            />
          </section>
        </DatoThemeSwitcher>
      )
    case 'DefaultHeroComponentRecord':
      return (
        <DatoThemeSwitcher theme={theme}>
          <section data-section="hero">
            <HgHero
              {...defaultProps}
              type="default"
              assetTwoOneRatio={
                <Asset
                  data={variant.defaultHeroModuleTwoOneAsset}
                  aspectRatio="2:1"
                />
              }
              assetFourFiveRatio={
                <Asset
                  data={variant.defaultHeroModuleFourFiveAsset}
                  aspectRatio="4:5"
                />
              }
            />
          </section>
        </DatoThemeSwitcher>
      )
    case 'PartnerPageHeroSection': {
      return (
        <DatoThemeSwitcher theme={theme}>
          <section data-section="hero">
            <HgHero
              {...defaultProps}
              type="splitImage"
              size={
                !subhead || structuredTextCharacterCount(subhead.text) < 200
                  ? 'short'
                  : 'long'
              }
              logoAsset={getLogo(data.logo)}
              assetFourFiveRatio={
                <Asset data={variant.fourFiveAsset} aspectRatio="4:5" />
              }
              assetOneOneRatio={
                variant.oneOneAsset && (
                  <Asset data={variant.oneOneAsset} aspectRatio="1:1" />
                )
              }
              assetThreeTwoRatio={
                variant.threeTwoAsset && (
                  <Asset data={variant.threeTwoAsset} aspectRatio="3:2" />
                )
              }
              additionalContent={
                variant.socialProof && (
                  <AdditionalContent content={variant.socialProof} />
                )
              }
            />
          </section>
        </DatoThemeSwitcher>
      )
    }
    default:
      return safeUnreachableCase(variant, null)
  }
}
